
import { StatsCard } from '@/components/shared/StatsCard'
import { StatsChart } from '@/components/shared/StatsChart';
import { StatsBarChart } from '@/components/shared/StatsBarChart';
import { useParams } from 'react-router-dom';

import {
	File,
	Database,
	Gauge,
	DollarSign,
	Trash,
	TrendingUpDown,
	CircleUserRound
} from "lucide-react";

import {
	useNetworkStats,
	useNetworkDataGrowthRate,
	useNetworkBlobSizeDistribution,
} from '@/hooks/useApi';
import { useMemo } from 'react';


export const OperatorDetailsPage = () => {
	const { id } = useParams<{ id: string }>()

	const { data: networkStats } = useNetworkStats()
	const { data: networkDataGrowthRate } = useNetworkDataGrowthRate();
	const { data: networkBlobSizeDistribution } = useNetworkBlobSizeDistribution();

	const growthDataInGB = useMemo(() => {
		return networkDataGrowthRate?.data.map((item: any) => {
			return {
				DAY: item.DAY,
				TOTAL_GB: Number(item.TOTAL_BYTES / 1_000_000_000)
			}
		})
	}, [networkDataGrowthRate])

	return (
		<div className='flex-1 overflow-y-auto overflow-x-hidden flex max-w-full 
  			[&::-webkit-scrollbar-track]:bg-gray-100
  			[&::-webkit-scrollbar-thumb]:bg-gray-300
  			dark:[&::-webkit-scrollbar-track]:bg-neutral-700
  			dark:[&::-webkit-scrollbar-thumb]:bg-neutral-500'
		>
			<div className='flex flex-col gap-1 w-full h-full'>
				<div className="flex flex-row gap-6 min-w-0 px-4">
					<StatsCard
						title="Operator ID"
						className='text-sm flex-1'
						valueStyleOverride='text-lg lg:text-2xl overflow-hidden whitespace-nowrap text-ellipsis'
						value={id}
						icon={<CircleUserRound className='w-4 h-4' />}
					/>
				</div>
				{/* Stat Cards */}
				<div
					className='grid grid-cols-1 gap-2 p-2
						md:grid-cols-2 md:px-4
						xl:grid-cols-4 xl:px-4
					'>
					<StatsCard
						title="Total Blobs"
						className='text-sm'
						valueStyleOverride='text-lg lg:text-2xl'
						tooltip='The total number of certified blobs by this operator.'
						value={Number(networkStats?.blobCertified).toLocaleString()}
						icon={<File className="h-4 w-4" />}
					/>
					<StatsCard
						title="Storage Capacity"
						className='text-sm'
						valueStyleOverride='text-lg lg:text-2xl'
						tooltip='The storage capacity of the operator.'
						value={`${Number(networkStats?.utilization?.consumed / 1_000_000_000_000).toLocaleString(undefined, {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})} / ${Number(networkStats?.utilization?.capacity / 1_000_000_000_000).toLocaleString(undefined, {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})} TB`}
						icon={<Database className="h-4 w-4" />}
					/>
					<StatsCard
						title="Storage Utilization"
						className='text-sm'
						valueStyleOverride='text-lg lg:text-2xl'
						tooltip='The percentage of the operator capacity currently being consumed.'
						value={`${Number(networkStats?.utilization?.utilization).toLocaleString(undefined, {
							minimumFractionDigits: 2,
							maximumFractionDigits: 2
						})} %`}
						icon={<Gauge className="h-4 w-4" />}
					/>
					<StatsCard
						title="Deleted Blobs"
						className='text-sm'
						valueStyleOverride='text-lg lg:text-2xl'
						value={`${networkStats?.blobDeleted.toLocaleString()}`}
						tooltip='The total number of deleted blobs by the operator.'
						icon={<Trash className="h-4 w-4" />}
					/>
				</div>

				{/* Stat Charts */}
				<div
					className='grid grid-cols-1 gap-2 p-2
						md:grid-cols-3 md:px-4
					'
				>
					<StatsBarChart
						className='text-sm'
						descriptionStyleOverride='text-xs md:text-md'
						title='Blob Size Distribution'
						data={networkBlobSizeDistribution?.data as any}
						xDataKey='BUCKET'
						yDataKey='TOTAL_COUNT'
						icon={<File className='h-4 w-4' />}
						description='Size distribution of blobs certified by the operator over the last 30 days.'
					/>
					<StatsChart
						className='text-sm'
						descriptionStyleOverride='text-xs md:text-md'
						title='Daily Operator Data Growth (30 Day)'
						data={growthDataInGB as any}
						xDataKey='DAY'
						yDataKey='TOTAL_GB'
						icon={<TrendingUpDown className='h-4 w-4' />}
						description='The amount of data added to the operator node over the last 30 days.'
					/>
					<StatsChart
						className='text-sm'
						descriptionStyleOverride='text-xs md:text-md'
						title='Operator Storage/Write Cost (30 Day)'
						data={[]}
						xDataKey=''
						yDataKey=''
						icon={<DollarSign className='h-4 w-4' />}
						description='The change in cost of storage and write operations charged by the operator over the last 30 days.'
					/>
				</div>
			</div>
		</div>
	);
}